import { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { rollupScores } from '../utils/rollups';
import { scoreWithConditions } from '../utils/conditionLoad';
import { loadScoringConditions } from '../../../services/oeSnapshotService';

const QUADS = ['reliability', 'efficiency', 'productivity', 'safety'];
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '_');
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const roundScores = (scores) => {
  const out = {};
  QUADS.forEach((q) => { out[q] = round2(scores?.[q]); });
  return out;
};
const overallOf = (scores) => round2(QUADS.reduce((a, q) => a + (scores[q] || 0), 0) / 4);

/**
 * Live comparison data for an organization — the real-data replacement for
 * mockData/comparisons.js. Everything is derived from Firestore:
 *
 *  - siteScores: latest site-scope OE snapshot per site, with a trend arrow
 *    computed against ~30 days earlier ('worsening' = entropy rising = red ↑,
 *    matching the existing UI convention)
 *  - regions: distinct `region` values on the org's sites
 *  - regionalAverages / enterpriseAverage: averaged from siteScores
 *  - deptScores / teamScores: aggregated live from the last 14 days of
 *    responses using the app's own scoring functions
 *
 * Shapes intentionally mirror the mock module so pages swap with minimal diff.
 */
export function useSiteComparisons(organizationId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // 1. Org structure
        const orgSnap = await getDoc(doc(db, 'Hugo_Organizations', organizationId));
        if (!orgSnap.exists()) throw new Error('Organization not found');
        // Include the doc id — consumers (report TRL fencing) rely on org.id.
        const org = { id: orgSnap.id, ...orgSnap.data() };
        const sites = org.sites || [];
        const departments = org.departments || [];
        const teams = org.teams || [];

        // Responding-unit targets per site (teams + team-less departments)
        const targetsBySite = {};
        for (const d of departments) {
          const dTeams = teams.filter((t) => t.departmentId === d.id);
          targetsBySite[d.siteId] = (targetsBySite[d.siteId] || 0) + Math.max(dTeams.length, 1);
        }

        // 2. All site-scope snapshots (newest first)
        const snapsQ = query(
          collection(db, 'Hugo_OESnapshots'),
          where('organizationId', '==', organizationId),
          where('scope.type', '==', 'site'),
          orderBy('calculatedAt', 'desc')
        );
        const snaps = (await getDocs(snapsQ)).docs.map((d2) => ({ id: d2.id, ...d2.data() }));

        const latestBySite = {};
        const monthAgoBySite = {};
        for (const s of snaps) {
          const sid = s.scope?.id;
          if (!sid) continue;
          if (!latestBySite[sid]) {
            latestBySite[sid] = s;
          } else if (
            !monthAgoBySite[sid] &&
            latestBySite[sid].calculatedAt.toMillis() - s.calculatedAt.toMillis() >= 28 * 24 * 3600 * 1000
          ) {
            monthAgoBySite[sid] = s;
          }
        }

        const siteScores = sites
          .filter((s) => latestBySite[s.id])
          .map((s) => {
            const latest = latestBySite[s.id];
            const prior = monthAgoBySite[s.id];
            // round2: raw float sums can carry FP noise (10.000000000000002)
            // which leaks into chart axes and tooltips.
            const scores = roundScores(latest.oeScores);
            let trend = 'stable';
            if (prior) {
              const diff = overallOf(latest.oeScores || {}) - overallOf(prior.oeScores || {});
              if (diff > 0.3) trend = 'worsening'; // entropy rising (red ↑)
              else if (diff < -0.3) trend = 'improving'; // entropy easing (green ↓)
            }
            return {
              siteId: s.id,
              siteName: s.name,
              regionId: s.region ? `region_${slug(s.region)}` : 'region_other',
              regionName: s.region || 'Other',
              scores,
              overall: overallOf(latest.oeScores || {}),
              warnings: latest.warnings || [],
              trend,
              lastSurveyResponses: latest.responseCount || 0,
              targetResponses: Math.max(targetsBySite[s.id] || 0, latest.responseCount || 0),
              perception: latest.perceptionScores || null,
              reality: latest.conditionScores || null,
              headcount: latest.basis?.headcount || 0,
              lowConfidence: latest.basis?.lowConfidence ?? (latest.responseCount || 0) < 5,
              basis: latest.basis?.text || `based on ${latest.responseCount || 0} responses`,
            };
          });

        // 3. Regions derived from site metadata
        const regionMap = {};
        for (const sc of siteScores) {
          (regionMap[sc.regionId] = regionMap[sc.regionId] || { id: sc.regionId, name: sc.regionName, sites: [] })
            .sites.push(sc);
        }
        const regions = Object.values(regionMap);

        // P2-38 (⚑Q-23 option B): regions and All-Sites are headcount-weighted means of the site
        // scores (headcount from membersIndex; a site with no members weighs its response count).
        const avgOf = (list) => {
          const out = {};
          const allHc = list.length > 0 && list.every((sc) => sc.headcount > 0);
          const weightOf = (sc) => (allHc ? sc.headcount : sc.lastSurveyResponses || 1);
          const total = list.reduce((a, sc) => a + weightOf(sc), 0);
          QUADS.forEach((q) => {
            out[q] = list.length && total > 0
              ? Number((list.reduce((a, sc) => a + sc.scores[q] * weightOf(sc), 0) / total).toFixed(2))
              : 0;
          });
          return out;
        };
        const regionalAverages = {};
        regions.forEach((r) => { regionalAverages[r.id] = avgOf(r.sites); });
        const enterpriseAverage = avgOf(siteScores);

        // 4. Dept/team scores from the last 14 days of responses (live math)
        const cutoff = Timestamp.fromMillis(Date.now() - 14 * 24 * 3600 * 1000);
        const respQ = query(
          collection(db, 'Hugo_Responses'),
          where('organizationId', '==', organizationId),
          where('completedAt', '>=', cutoff)
        );
        const responses = (await getDocs(respQ)).docs.map((d2) => {
          const r = d2.data();
          return {
            ...r,
            siteId: r.siteId ?? r.respondentScope?.siteId ?? null,
            departmentId: r.departmentId ?? r.respondentScope?.departmentId ?? null,
            teamId: r.teamId ?? r.respondentScope?.teamId ?? null,
          };
        });

        // Headcount-weighted rollup (P2-38) + condition load (P2-35), same math as the snapshots.
        let conditions = [];
        try { conditions = await loadScoringConditions(organizationId); } catch { /* perception only */ }
        const rollup = rollupScores(org, responses);
        const withReality = (node, scope) => {
          if (!node || !node.scores) return null;
          const r = scoreWithConditions({ perception: node.scores, conditions, scope, headcount: node.headcount, responseCount: node.responseCount, scoring: org.scoring });
          return {
            scores: roundScores(r.scores),
            perception: r.perception,
            reality: r.contribution,
            responseCount: node.responseCount,
            teamsCount: node.teamsCount,
            headcount: node.headcount,
            lowConfidence: node.lowConfidence,
            basis: node.basis,
          };
        };

        const deptScores = departments
          .map((d) => {
            const agg = withReality(rollup.departments[d.id], { type: 'department', id: d.id });
            return agg && { deptId: d.id, deptName: d.name, siteId: d.siteId, ...agg };
          })
          .filter(Boolean);

        const teamScores = teams
          .map((t) => {
            const agg = withReality(rollup.teams[t.id], { type: 'team', id: t.id });
            return agg && { teamId: t.id, teamName: t.name, departmentId: t.departmentId, ...agg };
          })
          .filter(Boolean);

        if (!cancelled) {
          setData({
            org,
            siteScores,
            regions,
            regionalAverages,
            enterpriseAverage,
            deptScores,
            teamScores,
          });
          setLoading(false);
        }
      } catch (err) {
        console.error('useSiteComparisons failed:', err);
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [organizationId]);

  return { comparisons: data, loading, error };
}
